/**
 * PublicNav — shared navigation bar for public pages (Landing, Normas, etc.)
 *
 * Extracted in S9-C7 to avoid nav drift between public pages after rebrand.
 * Uses navy background (#0A0F26), Logo V2 inverse, and action token for CTA.
 *
 * S10-C5: parametrizado con prop `navItems` opcional. Cuando no se pasa,
 * usa DEFAULT_NAV (Features/Frameworks/Pricing) — NormasPage sigue funcionando
 * sin cambios. LandingPage inyecta sus items (Features/Solutions/Pricing).
 * ADR-S10-003.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { Button } from "./ui/button";
import Logo from "./Logo";
import { useI18n } from "../i18n/I18nProvider";

const NAV_COPY = {
  en: {
    features: "Features",
    normas: "Frameworks",
    pricing: "Pricing",
    solutions: "Solutions",
    login: "Login",
    start: "Request Demo",
    english: "EN",
    spanish: "ES",
  },
  es: {
    features: "Funciones",
    normas: "Normas",
    pricing: "Precios",
    solutions: "Soluciones",
    login: "Iniciar Sesión",
    start: "Solicitar Demo",
    english: "EN",
    spanish: "ES",
  },
};

// Default nav items: Features → /#features, Frameworks → /normas, Pricing → /#pricing.
// Mantener este default garantiza que NormasPage (que no pasa navItems) no cambia.
const DEFAULT_NAV = [
  { key: "features", href: "/#features" },
  { key: "normas",   to: "/normas" },
  { key: "pricing",  href: "/#pricing" },
];

/**
 * @param {boolean} scrolled - activa backdrop-blur (NormasPage pasa true fijo;
 *                             LandingPage pasa estado de scroll listener).
 * @param {Array<{key: string, href?: string, to?: string}>} navItems - items de
 *        nav opcionales. Cada item: key (clave en NAV_COPY), href (ancla) o to
 *        (ruta React Router). Si no se pasa, usa DEFAULT_NAV.
 */
const PublicNav = ({ scrolled = false, navItems }) => {
  const { locale, setLocale } = useI18n();
  const lang = locale === "es" ? "es" : "en";
  const t = NAV_COPY[lang];
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = navItems || DEFAULT_NAV;

  // Renderiza un item de nav: Link si tiene `to`, <a> si tiene `href`
  const renderItem = (item, extraClassName, onClick) => {
    const label = t[item.key];
    if (item.to) {
      return (
        <Link
          key={item.key}
          to={item.to}
          className={extraClassName}
          onClick={onClick}
        >
          {label}
        </Link>
      );
    }
    return (
      <a
        key={item.key}
        href={item.href}
        className={extraClassName}
        onClick={onClick}
      >
        {label}
      </a>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-base ${
        scrolled ? "backdrop-blur-xl bg-[#0A0F26]/80 border-b border-white/10" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="public-nav-logo">
          <Logo variant="wordmark" className="text-[22px]" alt="ComplianceOS by MSSPRD" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400" aria-label="main navigation">
          {items.map((item) =>
            renderItem(item, "hover:text-white transition", undefined)
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setLocale(lang === "es" ? "en" : "es")}
            className="hidden md:flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition border border-white/10 px-2.5 py-1.5 rounded"
            aria-label={lang === "es" ? "Switch to English" : "Cambiar a Español"}
            data-testid="lang-toggle"
          >
            <Globe className="w-3 h-3" /> {lang === "es" ? t.english : t.spanish}
          </button>
          <Link
            to="/auth"
            className="hidden md:block text-sm text-zinc-400 hover:text-white transition"
          >
            {t.login}
          </Link>
          {/* S9: action token — solid #5733E8, WCAG AA ≥4.5:1 (ADR-S9-004) */}
          <Button
            onClick={() => navigate("/demo")}
            className="bg-action text-action-foreground hover:bg-action/90 font-semibold"
            data-testid="public-nav-cta"
          >
            {t.start}
          </Button>
          {/* Mobile menu toggle */}
          <button
            className="md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0A0F26]/95 backdrop-blur-xl">
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3 text-sm" aria-label="mobile navigation">
            {items.map((item) =>
              renderItem(item, "text-zinc-300 py-2", () => setMenuOpen(false))
            )}
            <Link to="/auth" onClick={() => setMenuOpen(false)} className="text-zinc-300 py-2">{t.login}</Link>
            <button
              onClick={() => { setLocale(lang === "es" ? "en" : "es"); setMenuOpen(false); }}
              className="flex items-center gap-1 text-zinc-400 py-2"
            >
              <Globe className="w-4 h-4" /> {lang === "es" ? "English" : "Español"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicNav;
